import { createContext, useContext, useEffect, useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import type { Deployment } from "@/features/deployment-selection/model/deployment";
import {
  clearActiveDeploymentId,
  setActiveDeploymentId,
} from "@/features/deployment-selection/model/deployment-events";

export interface SelectedDeploymentContextValue {
  deploymentId: string;
  selectedDeployment: Deployment;
  deployments: readonly Deployment[];
}

const SelectedDeploymentContext = createContext<SelectedDeploymentContextValue | null>(null);

interface SelectedDeploymentProviderProps {
  value: SelectedDeploymentContextValue;
  children: ReactNode;
}

export function SelectedDeploymentProvider({ value, children }: SelectedDeploymentProviderProps) {
  const deploymentIdRef = useRef(value.deploymentId);
  deploymentIdRef.current = value.deploymentId;

  useLayoutEffect(() => {
    setActiveDeploymentId(value.deploymentId);
  }, [value.deploymentId]);

  useEffect(
    () => () => {
      clearActiveDeploymentId(deploymentIdRef.current);
    },
    []
  );

  return (
    <SelectedDeploymentContext.Provider value={value}>
      {children}
    </SelectedDeploymentContext.Provider>
  );
}

export function useSelectedDeployment(): SelectedDeploymentContextValue {
  const value = useContext(SelectedDeploymentContext);

  if (!value) {
    throw new Error("useSelectedDeployment must be used within a SelectedDeploymentProvider.");
  }

  return value;
}
